import React, { useState } from 'react';
import { X, LogIn, User, UserCheck, ShieldCheck, KeyRound, Loader, Eye, EyeOff, Save, Lock, CreditCard, Smartphone, Building2, CheckSquare } from 'lucide-react';
import { G1, G2, GE, GG, INP, LBL, SS, PERM_KEYS } from '../../config/styles';
import { Alert, Overlay, Modal, PermsGrid } from '../shared';
import { contentSave, contentDelete } from '../../config/adminApi';

// ── إدارة إحصاءات من نحن ─────────────────────────────
function AboutStatsEditor({ settings }) {
  const [stats, setStats] = useState(() => {
    try {
      const s = settings?.about_stats;
      return Array.isArray(s) ? s : (typeof s === 'string' ? JSON.parse(s) : [
        { label_ar: 'كورس', label_en: 'Courses', value: '20+' },
        { label_ar: 'طالب', label_en: 'Students', value: '500+' },
        { label_ar: 'ساعة تعليمية', label_en: 'Hours', value: '200+' },
      ]);
    } catch { return []; }
  });

  const add    = () => setStats(p => [...p, { label_ar: '', label_en: '', value: '' }]);
  const remove = (i) => setStats(p => p.filter((_, idx) => idx !== i));
  const update = (i, field, val) => setStats(p => p.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ ...LBL, margin: 0 }}>📊 الإحصاءات</label>
        <button type="button" onClick={add}
          style={{ padding: '3px 10px', background: '#eef2ff', color: '#5a67d8', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
          + إضافة
        </button>
      </div>
      {stats.map((s, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 32px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
          <input value={s.label_ar} onChange={e => update(i, 'label_ar', e.target.value)}
            placeholder="التسمية عربي" style={{ ...INP, fontSize: '12px', margin: 0 }} />
          <input value={s.label_en} onChange={e => update(i, 'label_en', e.target.value)}
            placeholder="English label" dir="ltr" style={{ ...INP, fontSize: '12px', margin: 0 }} />
          <input value={s.value} onChange={e => update(i, 'value', e.target.value)}
            placeholder="500+" style={{ ...INP, fontSize: '13px', fontWeight: 'bold', textAlign: 'center', margin: 0 }} />
          <button type="button" onClick={() => remove(i)}
            style={{ padding: '6px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>✕</button>
        </div>
      ))}
      {/* hidden input to pass stats to form */}
      <input type="hidden" name="aboutStats" value={JSON.stringify(stats)} />
    </div>
  );
}

// ── Employee Login ──────────────────────────
export function EmpLoginModal({ t, isRTL, onSubmit, onClose, empErr, empOk, empLoad, empUsernameRef, empPasswordRef }) {
  const [showPwd, setShowPwd] = useState(false);
  return (
    <Overlay onClose={onClose} dark>
      <Modal dark maxWidth="400px">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '40px', height: '40px', background: GE, border: '2px solid #334155', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><KeyRound size={20} color="#94a3b8" /></div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '15px', color: 'white' }}>{t.empLogin}</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>{t.empSubtitle}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ height: '1px', background: '#1e293b', marginBottom: '20px' }} />
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ ...LBL, color: '#94a3b8' }}>{t.username}</label>
            <input ref={empUsernameRef} defaultValue="" placeholder={t.enterUsername} required autoComplete="username"
              style={{ ...INP, background: '#1e293b', border: `2px solid ${empErr ? '#EF4444' : '#334155'}`, color: 'white' }} />
          </div>
          <div>
            <label style={{ ...LBL, color: '#94a3b8' }}>{t.password}</label>
            <div style={{ position: 'relative' }}>
              <input ref={empPasswordRef} type={showPwd ? 'text' : 'password'} defaultValue="" placeholder="••••••••" required autoComplete="current-password"
                style={{ ...INP, background: '#1e293b', border: `2px solid ${empErr ? '#EF4444' : '#334155'}`, color: 'white', paddingRight: '42px' }} />
              <button type="button" onClick={() => setShowPwd(x => !x)} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [isRTL ? 'left' : 'right']: '12px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, display: 'flex' }}>
                {showPwd ? <Eye size={17} /> : <EyeOff size={17} />}
              </button>
            </div>
          </div>
          {empErr && <Alert msg={empErr} type="error" />}
          {empOk  && <Alert msg={empOk}  type="success" />}
          <button type="submit" disabled={empLoad} style={{ padding: '12px', background: empLoad ? '#334155' : GE, color: empLoad ? '#64748b' : 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: empLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {empLoad ? <><Loader size={15} style={SS} /> ...</> : <><ShieldCheck size={15} /> {t.loginBtn}</>}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '10px', color: '#1e293b', letterSpacing: '2px' }}>F8 · Ctrl+Alt+A · gawhara</div>
      </Modal>
    </Overlay>
  );
}

// ── Customer Modal ──────────────────────────
export function CustModal({ t, isRTL, onSubmit, onClose, custTab, setCustTab, custForm, setCustForm, custErr, setCustErr, custOk, custLoad, onForgotPassword }) {
  const [showPwd, setShowPwd] = useState(false);
  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="420px">
        <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: '10px', padding: '4px', marginBottom: '20px', gap: '4px' }}>
          {['login', 'register', 'guest'].map(tab => (
            <button key={tab} onClick={() => { setCustTab(tab); setCustErr(''); }}
              style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', background: custTab === tab ? 'white' : 'transparent', color: custTab === tab ? '#667eea' : '#6B7280', boxShadow: custTab === tab ? '0 2px 6px rgba(0,0,0,0.1)' : 'none', transition: 'all .2s' }}>
              {tab === 'login' ? <><LogIn size={11} style={{ verticalAlign: 'middle', margin: '0 2px' }} /> {t.custLogin}</>
                : tab === 'register' ? <><User size={11} style={{ verticalAlign: 'middle', margin: '0 2px' }} /> {t.custRegister}</>
                  : <><UserCheck size={11} style={{ verticalAlign: 'middle', margin: '0 2px' }} /> {t.guestLabel}</>}
            </button>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          <div style={{ width: '54px', height: '54px', background: custTab === 'guest' ? 'linear-gradient(135deg,#f59e0b,#d97706)' : G1, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', boxShadow: '0 6px 16px rgba(102,126,234,0.3)' }}>
            {custTab === 'login' ? <LogIn size={23} color="white" /> : custTab === 'register' ? <User size={23} color="white" /> : <UserCheck size={23} color="white" />}
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '4px' }}>
            {custTab === 'login' ? t.custWelcome : custTab === 'register' ? t.custWelcomeReg : t.guestWelcome}
          </h2>
          <p style={{ color: '#6B7280', fontSize: '12px' }}>
            {custTab === 'login' ? t.custSubtitle : custTab === 'register' ? t.custSubtitleReg : t.guestSubtitle}
          </p>
        </div>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {custTab === 'guest' && (
            <>
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 12px', fontSize: '12px', color: '#92400E' }}>{t.guestNote}</div>
              <div><label style={LBL}>{t.name}</label><input value={custForm.name} onChange={e => setCustForm({ ...custForm, name: e.target.value })} placeholder={t.enterName} required style={INP} /></div>
              <div><label style={LBL}>{t.phone}</label><input value={custForm.phone} onChange={e => setCustForm({ ...custForm, phone: e.target.value })} placeholder={t.enterPhone} type="tel" style={INP} /></div>
              {custErr && <Alert msg={custErr} type="error" />}
              {custOk  && <Alert msg={custOk}  type="success" />}
              <button type="submit" disabled={custLoad} style={{ padding: '12px', background: custLoad ? '#9CA3AF' : 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: custLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {custLoad ? <><Loader size={14} style={SS} /> ...</> : <><UserCheck size={14} /> {t.guestBtn}</>}
              </button>
            </>
          )}
          {custTab !== 'guest' && (
            <>
              {custTab === 'register' && (
                <>
                  <div><label style={LBL}>{t.name}</label><input value={custForm.name} onChange={e => { setCustForm({ ...custForm, name: e.target.value }); setCustErr(''); }} placeholder={t.enterName} required style={{ ...INP, borderColor: custErr ? '#EF4444' : '#E5E7EB' }} /></div>
                  <div>
                    <label style={LBL}>👤 {t.username || 'اسم المستخدم'}</label>
                    <input value={custForm.username || ''} onChange={e => { setCustForm({ ...custForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }); setCustErr(''); }}
                      placeholder="مثال: ahmed_2025" required style={{ ...INP, borderColor: custErr ? '#EF4444' : '#E5E7EB', direction: 'ltr' }} />
                    <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>حروف إنجليزية وأرقام و _ فقط</div>
                  </div>
                  <div><label style={LBL}>{t.email}</label><input type="email" value={custForm.email} onChange={e => { setCustForm({ ...custForm, email: e.target.value }); setCustErr(''); }} placeholder={t.enterEmail} required style={{ ...INP, borderColor: custErr ? '#EF4444' : '#E5E7EB' }} /></div>
                  <div><label style={LBL}>{t.phone}</label><input value={custForm.phone} onChange={e => setCustForm({ ...custForm, phone: e.target.value })} placeholder={t.enterPhone} type="tel" style={INP} /></div>
                </>
              )}
              {custTab === 'login' && (
                <div>
                  <label style={LBL}>👤 {t.username || 'اسم المستخدم'}</label>
                  <input value={custForm.username || ''} onChange={e => { setCustForm({ ...custForm, username: e.target.value.toLowerCase() }); setCustErr(''); }}
                    placeholder={t.enterUsername || 'أدخل اسم المستخدم'} required
                    style={{ ...INP, borderColor: custErr ? '#EF4444' : '#E5E7EB', direction: 'ltr' }} />
                </div>
              )}
              <div>
                <label style={LBL}>{t.password}</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} value={custForm.password} onChange={e => { setCustForm({ ...custForm, password: e.target.value }); setCustErr(''); }} placeholder="••••••••" required style={{ ...INP, paddingRight: '42px', borderColor: custErr ? '#EF4444' : '#E5E7EB' }} />
                  <button type="button" onClick={() => setShowPwd(x => !x)} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [isRTL ? 'left' : 'right']: '12px', background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    {showPwd ? <Eye size={17} /> : <EyeOff size={17} />}
                  </button>
                </div>
              </div>
              {custErr && <Alert msg={custErr} type="error" />}
              {custOk  && <Alert msg={custOk}  type="success" />}
              <button type="submit" disabled={custLoad} style={{ padding: '12px', background: custLoad ? '#9CA3AF' : G1, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: custLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(102,126,234,0.35)' }}>
                {custLoad ? <><Loader size={14} style={SS} /> ...</> : custTab === 'login' ? <><LogIn size={14} /> {t.loginBtn}</> : <><User size={14} /> {t.registerBtn}</>}
              </button>
              <div style={{ textAlign: 'center', fontSize: '12px', color: '#6B7280' }}>
                {custTab === 'login' ? t.noAccount : t.haveAccount}
                <button type="button" onClick={() => { setCustTab(custTab === 'login' ? 'register' : 'login'); setCustErr(''); }} style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', margin: '0 4px' }}>
                  {custTab === 'login' ? t.registerBtn : t.loginBtn}
                </button>
              </div>
              {custTab === 'login' && onForgotPassword && (
                <div style={{ textAlign: 'center' }}>
                  <button type="button" onClick={onForgotPassword} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                    🔑 نسيت كلمة المرور؟
                  </button>
                </div>
              )}
            </>
          )}
          <button type="button" onClick={onClose} style={{ padding: '10px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{t.cancel}</button>
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Payment Modal ──────────────────────────
export function PaymentModal({ activeCourse, customer, settings, t, lang, onClose, onCardPay, onManualPay, onFreeEnroll, payLoad, payErr }) {
  const [method,       setMethod]       = useState('card');
  const [note,         setNote]         = useState('');
  const [couponCode,   setCouponCode]   = useState('');
  const [couponData,   setCouponData]   = useState(null);  // الكوبون المتحقق منه
  const [couponErr,    setCouponErr]    = useState('');
  const [couponLoad,   setCouponLoad]   = useState(false);

  const originalPrice = activeCourse?.price || 0;
  const discount = couponData
    ? couponData.type === 'percent'
      ? Math.round(originalPrice * couponData.value / 100)
      : Math.min(couponData.value, originalPrice)
    : 0;
  const finalPrice = Math.max(0, originalPrice - discount);

  const handleCheckCoupon = async () => {
    if (!couponCode.trim()) { setCouponErr('أدخل كود الخصم'); return; }
    setCouponLoad(true); setCouponErr(''); setCouponData(null);

    const { supabase } = await import('../../config/supabase');
    const { data: c } = await supabase
      .from('coupons')
      .select('*')
      .eq('code', couponCode.trim().toUpperCase())
      .eq('is_active', true)
      .single();

    setCouponLoad(false);

    if (!c) { setCouponErr('❌ كود الخصم غير صحيح أو منتهي'); return; }
    if (c.expires_at && new Date(c.expires_at) < new Date()) { setCouponErr('❌ انتهت صلاحية هذا الكود'); return; }
    if (c.max_uses && c.used_count >= c.max_uses) { setCouponErr('❌ تم استنفاد هذا الكود'); return; }
    if (c.course_id && c.course_id !== activeCourse.id) { setCouponErr('❌ هذا الكود لا ينطبق على هذا الكورس'); return; }

    setCouponData(c);
  };

  const handleRemoveCoupon = () => { setCouponData(null); setCouponCode(''); setCouponErr(''); };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="460px">
        {!customer ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Lock size={36} style={{ color: '#667eea', marginBottom: '10px' }} />
            <p style={{ color: '#374151', fontSize: '15px', fontWeight: 'bold' }}>{t.loginFirst}</p>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px', color: '#1F2937' }}>{t.payTitle}</h2>

            {/* السعر + الخصم */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#6B7280', fontSize: '13px', margin: '0 0 6px' }}>
                {lang === 'ar' ? activeCourse.name_ar : activeCourse.name_en}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {couponData ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: '#9CA3AF', fontSize: '14px' }}>{originalPrice} جنيه</span>
                    <span style={{ fontWeight: 'bold', color: '#27ae60', fontSize: '20px' }}>{finalPrice} جنيه</span>
                    <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: '11px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '20px' }}>
                      وفّرت {discount} جنيه{couponData.type === 'percent' ? ` (${couponData.value}%)` : ''}
                    </span>
                  </>
                ) : (
                  <strong style={{ color: '#667eea', fontSize: '20px' }}>{originalPrice} جنيه</strong>
                )}
              </div>
            </div>

            {/* حقل الكوبون */}
            <div style={{ marginBottom: '16px' }}>
              {!couponData ? (
                <>
                  <label style={{ ...LBL, marginBottom: '6px' }}>🎟️ كود الخصم (اختياري)</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      value={couponCode}
                      onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponErr(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleCheckCoupon()}
                      placeholder="مثال: GAWHARA20"
                      style={{ ...INP, flex: 1, letterSpacing: '1px', fontWeight: 'bold', margin: 0 }}
                      dir="ltr"
                    />
                    <button onClick={handleCheckCoupon} disabled={couponLoad}
                      style={{ padding: '0 16px', background: couponLoad ? '#9CA3AF' : G1, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {couponLoad ? '...' : 'تطبيق'}
                    </button>
                  </div>
                  {couponErr && <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '5px' }}>{couponErr}</div>}
                </>
              ) : (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#16a34a' }}>✅ كود "{couponData.code}" مطبّق</span>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                      {couponData.type === 'percent' ? `خصم ${couponData.value}%` : `خصم ${couponData.value} جنيه`}
                    </div>
                  </div>
                  <button onClick={handleRemoveCoupon}
                    style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                    ✕ إزالة
                  </button>
                </div>
              )}
            </div>

            {/* طرق الدفع — تختفي لو الكورس مجاني بالكوبون */}
            {finalPrice > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              {[
                { id: 'card',     icon: <CreditCard size={18} />, label: t.payCard,     desc: t.payCardDesc,     color: '#667eea' },
                { id: 'vodafone', icon: <Smartphone size={18} />, label: t.payWallet,   desc: t.payWalletDesc,   color: '#E11D48' },
                { id: 'transfer', icon: <Building2  size={18} />, label: t.payTransfer, desc: t.payTransferDesc, color: '#059669' },
              ].map(m => (
                <div key={m.id} onClick={() => setMethod(m.id)}
                  style={{ border: `2px solid ${method === m.id ? m.color : '#E5E7EB'}`, borderRadius: '10px', padding: '12px 8px', cursor: 'pointer', textAlign: 'center', background: method === m.id ? m.color + '15' : 'white', transition: 'all .2s' }}>
                  <div style={{ color: m.color, marginBottom: '5px', display: 'flex', justifyContent: 'center' }}>{m.icon}</div>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: method === m.id ? m.color : '#374151' }}>{m.label}</div>
                  <div style={{ fontSize: '9px', color: '#6B7280', marginTop: '2px' }}>{m.desc}</div>
                </div>
              ))}
            </div>
            )}

            {(method === 'vodafone' || method === 'transfer') && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: '#065F46' }}>
                {method === 'vodafone' && <div>📱 <strong>فودافون كاش:</strong> {settings.vodafone_number}</div>}
                {method === 'transfer' && (
                  <>
                    {/* InstaPay متعدد */}
                    {(() => {
                      const list = settings.instapay_numbers;
                      const hasMulti = Array.isArray(list) && list.length > 0;
                      if (hasMulti) {
                        return list.map((item, i) => (
                          <div key={i} style={{ marginBottom: i < list.length-1 ? '6px' : 0 }}>
                            💳 <strong>{item.label || 'InstaPay'}:</strong>{' '}
                            <span dir="ltr" style={{ fontFamily: 'monospace' }}>{item.number}</span>
                          </div>
                        ));
                      }
                      return <div>💳 <strong>InstaPay:</strong> {settings.instapay_number}</div>;
                    })()}
                    {settings.bank_account && <div style={{ marginTop: '4px' }}>🏦 {settings.bank_account}</div>}
                  </>
                )}
                <div style={{ marginTop: '6px', color: '#374151' }}>بعد الدفع، أدخل رقم المعاملة وابعت للمراجعة</div>
              </div>
            )}
            {(method === 'vodafone' || method === 'transfer') && (
              <div style={{ marginBottom: '14px' }}>
                <label style={LBL}>{t.receiptNote}</label>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder={t.enterNote} style={INP} />
              </div>
            )}

            {payErr && <div style={{ marginBottom: '10px' }}><Alert msg={payErr} type="error" /></div>}

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              {finalPrice <= 0 ? (
                /* ✅ السعر صفر (كوبون 100%) — تفعيل فوري بدون دفع */
                <button onClick={() => onFreeEnroll(activeCourse, couponData)}
                  disabled={payLoad}
                  style={{ flex: 1, padding: '12px', background: payLoad ? '#9CA3AF' : GG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: payLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {payLoad ? <><Loader size={15} style={SS} /> ...</> : <><CheckSquare size={15} /> {lang === 'ar' ? 'تفعيل الكورس مجانًا 🎉' : 'Activate for Free 🎉'}</>}
                </button>
              ) : method === 'card' ? (
                <button onClick={() => onCardPay(activeCourse, finalPrice, couponData)}
                  disabled={payLoad}
                  style={{ flex: 1, padding: '12px', background: payLoad ? '#9CA3AF' : G1, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: payLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {payLoad ? <><Loader size={15} style={SS} /> ...</> : <><CreditCard size={15} /> {t.payNow} {finalPrice} جنيه</>}
                </button>
              ) : (
                <button onClick={() => onManualPay(activeCourse, method, note, finalPrice, couponData)}
                  disabled={payLoad || !note.trim()}
                  style={{ flex: 1, padding: '12px', background: (payLoad || !note.trim()) ? '#9CA3AF' : GG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: (payLoad || !note.trim()) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {payLoad ? <><Loader size={15} style={SS} /> ...</> : <><CheckSquare size={15} /> {t.sendReceipt} — {finalPrice} جنيه</>}
                </button>
              )}
              <button onClick={onClose} style={{ padding: '12px 18px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{t.cancel}</button>
            </div>
          </>
        )}
      </Modal>
    </Overlay>
  );
}

// ── Course Modal ─────────────────────────────
export function CourseModal({ editing, t, onSubmit, onClose, saveLoad, onUploadThumbnail, onUploadImage, categories = [], instructors = [] }) {
  const [preview,     setPreview]     = useState(editing?.thumbnail_url || '');
  const [avatarPrev,  setAvatarPrev]  = useState(editing?.instructor_avatar || '');
  const [bannerPrev,  setBannerPrev]  = useState(editing?.banner_url || '');
  const [uploading,   setUploading]   = useState(false);
  const [uploadErr,   setUploadErr]   = useState('');
  const fileRef   = React.useRef();
  const avatarRef = React.useRef();
  const bannerRef = React.useRef();

  // رفع صورة المدرب / البانر من الكمبيوتر
  const handleImageUpload = async (e, setter, prefix) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setUploadErr('الصورة أكبر من 3MB'); return; }
    setUploadErr(''); setUploading(true);
    const url = await onUploadImage(file, prefix);
    if (url) setter(url);
    else setUploadErr('فشل الرفع، حاول تاني');
    setUploading(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setUploadErr('الصورة أكبر من 3MB'); return; }
    setUploadErr(''); setUploading(true);
    const url = await onUploadThumbnail(file);
    if (url) setPreview(url);
    else setUploadErr('فشل الرفع، حاول تاني');
    setUploading(false);
  };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="480px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>{editing ? t.editCourse : t.addCourse}</h2>
        <form onSubmit={(e) => onSubmit(e, preview, avatarPrev, bannerPrev)} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div><label style={LBL}>{t.nameAr}</label><input name="nameAr" required defaultValue={editing?.name_ar} style={INP} /></div>
          <div><label style={LBL}>{t.nameEn}</label><input name="nameEn" required defaultValue={editing?.name_en} style={INP} /></div>
          <div><label style={LBL}>{t.price} (جنيه)</label><input name="price" type="number" required defaultValue={editing?.price} style={INP} /></div>
          <div><label style={LBL}>{t.icon} (Emoji)</label><input name="icon" required defaultValue={editing?.icon || '📚'} style={INP} /></div>

          {/* ── حقول صفحة الكورس الاحترافية ── */}
          {/* التصنيف + المدرب (من الجداول الجديدة) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={LBL}>🗂️ التصنيف</label>
              <select name="categoryId" defaultValue={editing?.category_id || ''} style={INP}>
                <option value="">— بدون تصنيف —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name_ar}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>👨‍🏫 المدرب</label>
              <select name="instructorId" defaultValue={editing?.instructor_id || ''} style={INP}>
                <option value="">— بدون مدرب —</option>
                {instructors.map(i => <option key={i.id} value={i.id}>{i.name_ar}</option>)}
              </select>
            </div>
          </div>
          <div><label style={LBL}>📝 وصف الكورس (عن الكورس)</label><textarea name="descriptionAr" defaultValue={editing?.description_ar || ''} placeholder="اكتب وصف كامل للكورس وهيتعلم إيه الطالب..." style={{ ...INP, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }} /></div>
          <div><label style={LBL}>✨ مميزات الكورس (كل ميزة في سطر)</label><textarea name="features" defaultValue={(Array.isArray(editing?.features) ? editing.features.map(f => typeof f === 'string' ? f : (f.ar || f.en || '')).join('\n') : '')} placeholder={'وصول مدى الحياة\nشهادة بعد الكورس\nدعم فني\nضمان استرداد 7 أيام'} style={{ ...INP, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={LBL}>👨‍🏫 اسم المدرب</label><input name="instructorName" defaultValue={editing?.instructor_name || ''} placeholder="بشمهندس ميدو" style={INP} /></div>
            <div>
              <label style={LBL}>🖼️ صورة المدرب</label>
              <div onClick={() => avatarRef.current?.click()}
                style={{ border: '2px dashed #C7D2FE', borderRadius: '10px', padding: '8px', textAlign: 'center', cursor: 'pointer', background: '#F9FAFB', minHeight: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <input ref={avatarRef} type="file" accept="image/*" onChange={e => handleImageUpload(e, setAvatarPrev, 'instructor')} style={{ display: 'none' }} />
                {avatarPrev
                  ? <img src={avatarPrev} alt="avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '12px', color: '#667eea' }}>📤 ارفع صورة المدرب</span>}
              </div>
            </div>
          </div>
          <div><label style={LBL}>📄 نبذة عن المدرب</label><input name="instructorBio" defaultValue={editing?.instructor_bio || ''} placeholder="خبير في..." style={INP} /></div>
          <div>
            <label style={LBL}>👥 رقم طلاب إضافي للدعاية (يتجمع على العدد الفعلي)</label>
            <input name="bonusStudents" type="number" min="0" defaultValue={editing?.bonus_students || 0} placeholder="2000" style={INP} />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>
              📊 العدد المعروض = المشتركين الفعليين + الرقم ده. التقييم بيتحسب أوتوماتيك من آراء الطلاب.
            </div>
          </div>
          <div><label style={LBL}>🗓️ المواعيد (نص حر)</label><input name="scheduleText" defaultValue={editing?.schedule_text_ar || ''} placeholder="السبت والثلاثاء والخميس 9 مساءً" style={INP} /></div>
          <div>
            <label style={LBL}>🖼️ صورة البانر/الإعلان</label>
            <div onClick={() => bannerRef.current?.click()}
              style={{ border: '2px dashed #C7D2FE', borderRadius: '10px', padding: '10px', textAlign: 'center', cursor: 'pointer', background: '#F9FAFB' }}>
              <input ref={bannerRef} type="file" accept="image/*" onChange={e => handleImageUpload(e, setBannerPrev, 'banner')} style={{ display: 'none' }} />
              {bannerPrev
                ? <img src={bannerPrev} alt="banner" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '8px' }} />
                : <span style={{ fontSize: '12px', color: '#667eea' }}>📤 ارفع صورة إعلان الكورس (بانر)</span>}
            </div>
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label style={LBL}>🖼️ صورة الكورس (Thumbnail)</label>
            <div style={{ border: '2px dashed #C7D2FE', borderRadius: '12px', padding: '16px', textAlign: 'center', background: '#F9FAFB', cursor: 'pointer', position: 'relative' }}
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#667eea'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#C7D2FE'; }}
              onDrop={async e => { e.preventDefault(); e.currentTarget.style.borderColor = '#C7D2FE'; const file = e.dataTransfer.files[0]; if (file) { const fakeE = { target: { files: [file] } }; await handleFile(fakeE); } }}>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
              {uploading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <Loader size={28} color="#667eea" style={SS} />
                  <span style={{ fontSize: '12px', color: '#667eea' }}>جاري الرفع...</span>
                </div>
              ) : preview ? (
                <div style={{ position: 'relative' }}>
                  <img src={preview} alt="thumbnail" style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '8px' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'all .2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.4)'; e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.style.opacity = '0'; }}>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>🔄 تغيير الصورة</span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <div style={{ fontSize: '32px' }}>🖼️</div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#667eea' }}>اسحب الصورة هنا أو اضغط للاختيار</div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF' }}>PNG, JPG, WebP — الحد الأقصى 3MB</div>
                </div>
              )}
            </div>
            {uploadErr && <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>⚠️ {uploadErr}</div>}
            {preview && (
              <button type="button" onClick={() => setPreview('')}
                style={{ marginTop: '6px', fontSize: '11px', color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                ✕ إزالة الصورة
              </button>
            )}
          </div>

          <div>
            <label style={LBL}>{t.driveIntro}</label>
            <input name="driveIntro" defaultValue={editing?.drive_intro_id || ''} placeholder="https://vimeo.com/123456789" style={INP} />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>🎬 الصق رابط الفيديو التشويقي من Vimeo (أو YouTube)</div>
          </div>
          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={t} bg={G1} />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Lesson Modal ─────────────────────────────
export function LessonModal({ editing, managingCourse, lessons, t, onSubmit, onClose, saveLoad, onUploadFile }) {
  const existing = lessons.filter(l => l.course_id === managingCourse?.id).length;
  const [atts, setAtts] = useState(Array.isArray(editing?.attachments) ? editing.attachments : []);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const addAtt    = () => setAtts(p => [...p, { name: '', url: '' }]);
  const removeAtt = (i) => setAtts(p => p.filter((_, idx) => idx !== i));
  const updateAtt = (i, key, val) => setAtts(p => p.map((a, idx) => idx === i ? { ...a, [key]: val } : a));
  // رفع ملف لمرفق معيّن
  const uploadAtt = async (i, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { alert('الملف أكبر من 20MB'); return; }
    setUploadingIdx(i);
    const res = await onUploadFile(file);
    if (res) setAtts(p => p.map((a, idx) => idx === i ? { name: a.name || res.name, url: res.url } : a));
    else alert('فشل رفع الملف، حاول تاني');
    setUploadingIdx(null);
  };
  // فلترة المرفقات الفاضية قبل الإرسال
  const cleanAtts = () => atts.filter(a => a.url && a.url.trim());

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="460px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>{editing ? t.editLesson : t.addLesson}</h2>
        <form onSubmit={(e) => onSubmit(e, cleanAtts())} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div><label style={LBL}>{t.titleAr}</label><input name="titleAr" required defaultValue={editing?.title_ar} style={INP} /></div>
          <div><label style={LBL}>{t.titleEn}</label><input name="titleEn" required defaultValue={editing?.title_en} style={INP} /></div>

          {/* Google Drive */}
          <div>
            <label style={LBL}>📁 رابط Google Drive</label>
            <input name="driveId" defaultValue={editing?.drive_file_id || ''} placeholder="https://drive.google.com/file/d/xxxxx/view" style={INP} />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>الصق رابط الفيديو من Google Drive</div>
          </div>

          {/* Vimeo — اختياري */}
          <div>
            <label style={LBL}>🎬 Vimeo <span style={{ fontWeight: 'normal', color: '#9CA3AF', fontSize: '11px' }}>(اختياري — لو حطيته هيشتغل بدل Drive)</span></label>
            <input name="vimeoId" defaultValue={editing?.vimeo_id || ''} placeholder="https://vimeo.com/123456789" style={INP} />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>أو YouTube — بيشتغل أوتوماتيك</div>
          </div>

          {/* Bunny Stream — اختياري */}
          <div>
            <label style={LBL}>🐰 Bunny Stream <span style={{ fontWeight: 'normal', color: '#9CA3AF', fontSize: '11px' }}>(اختياري — بديل فيمو)</span></label>
            <input name="bunnyVideoId" defaultValue={editing?.bunny_video_id || ''} placeholder="كود الـ Embed أو libraryId/videoId" style={INP} dir="ltr" />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>الصق كود الـ Embed من Bunny Stream (أو libraryId/guid)</div>
          </div>

          {/* مصدر الفيديو */}
          <div>
            <label style={LBL}>🎯 مصدر الفيديو</label>
            <select name="videoSource" defaultValue={editing?.video_source || ''} style={INP}>
              <option value="">تلقائي (الموجود — يفضّل فيمو)</option>
              <option value="vimeo">Vimeo</option>
              <option value="bunny">Bunny Stream</option>
              <option value="drive">Google Drive</option>
            </select>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>"تلقائي" بيشتغل زي ما هو دلوقتي. اختار Bunny عشان تجرّبه على الحلقة دي بس.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={LBL}>{t.duration}</label><input name="duration" defaultValue={editing?.duration || ''} placeholder="15:30" style={INP} /></div>
            <div><label style={LBL}>{t.orderNum}</label><input name="orderNum" type="number" defaultValue={editing?.order_num || existing + 1} style={INP} /></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
            <input type="checkbox" name="isFree" id="isFree" defaultChecked={editing?.is_free || false} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#667eea' }} />
            <label htmlFor="isFree" style={{ cursor: 'pointer', fontWeight: 'bold', color: '#374151', fontSize: '13px' }}>{t.isFree}</label>
          </div>

          {/* شرح الحلقة */}
          <div><label style={LBL}>📝 شرح الحلقة</label><textarea name="descriptionAr" defaultValue={editing?.description_ar || ''} placeholder="اكتب شرح الحلقة وهتتعلم إيه فيها..." style={{ ...INP, minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' }} /></div>

          {/* مرفقات الحلقة */}
          <div>
            <label style={LBL}>📎 مرفقات الحلقة (ارفع ملف أو الصق رابط)</label>
            {atts.map((a, i) => (
              <div key={i} style={{ background: '#F9FAFB', borderRadius: '10px', padding: '10px', marginBottom: '8px', border: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                  <input value={a.name} onChange={e => updateAtt(i, 'name', e.target.value)} placeholder="اسم الملف (مثلاً: ملخص الحلقة)" style={{ ...INP, flex: 1 }} />
                  <button type="button" onClick={() => removeAtt(i)} style={{ background: '#FEE2E2', color: '#EF4444', border: 'none', borderRadius: '8px', padding: '0 12px', cursor: 'pointer', fontWeight: 'bold', alignSelf: 'stretch' }}>✕</button>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input value={a.url} onChange={e => updateAtt(i, 'url', e.target.value)} placeholder="https://... (أو ارفع ملف)" style={{ ...INP, flex: 1, fontSize: '12px' }} />
                  <label style={{ background: uploadingIdx === i ? '#9CA3AF' : '#EEF2FF', color: uploadingIdx === i ? 'white' : '#667eea', borderRadius: '8px', padding: '9px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {uploadingIdx === i ? <><Loader size={13} style={SS} /> رفع...</> : '📤 ملف'}
                    <input type="file" onChange={e => uploadAtt(i, e.target.files?.[0])} disabled={uploadingIdx !== null} style={{ display: 'none' }} />
                  </label>
                </div>
                {a.url && <div style={{ fontSize: '10px', color: '#10b981', marginTop: '4px' }}>✓ الملف جاهز</div>}
              </div>
            ))}
            <button type="button" onClick={addAtt} style={{ fontSize: '12px', color: '#667eea', background: '#EEF2FF', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold' }}>+ إضافة مرفق</button>
            <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '5px' }}>📄 يدعم PDF, Word, أي ملف — الحد الأقصى 20MB</div>
          </div>

          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={t} bg={G1} />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Service Modal ────────────────────────────
export function ServiceModal({ editing, t, onSubmit, onClose, saveLoad, onUploadImage }) {
  const [features, setFeatures] = React.useState(() => {
    const f = editing?.features;
    if (Array.isArray(f) && f.length) return f.map(x => typeof x === 'string' ? x : (x.text || ''));
    return [''];
  });
  const [isFeatured, setIsFeatured] = React.useState(editing?.is_featured || false);

  const addFeature    = () => setFeatures(p => [...p, '']);
  const removeFeature = (i) => setFeatures(p => p.filter((_, idx) => idx !== i));
  const updateFeature = (i, v) => setFeatures(p => p.map((x, idx) => idx === i ? v : x));

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanFeatures = features.map(f => String(f).trim()).filter(Boolean);
    onSubmit(e, { features: cleanFeatures, is_featured: isFeatured });
  };

  const secStyle = { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' };
  const secTitle = { fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: '2px' };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="540px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>{editing ? t.editService : t.addService}</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '75vh', overflowY: 'auto', paddingLeft: '4px' }}>

          {/* أساسي */}
          <div style={secStyle}>
            <p style={secTitle}>📦 بيانات الباقة الأساسية</p>
            <div><label style={LBL}>{t.nameAr}</label><input name="nameAr" required defaultValue={editing?.name_ar} style={INP} /></div>
            <div><label style={LBL}>{t.nameEn}</label><input name="nameEn" defaultValue={editing?.name_en} style={INP} /></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}><label style={LBL}>{t.price} ($)</label><input name="price" type="number" required defaultValue={editing?.price} style={INP} /></div>
              <div style={{ width: '110px' }}><label style={LBL}>{t.icon}</label><input name="icon" required defaultValue={editing?.icon || '💼'} style={INP} /></div>
            </div>
            <div><label style={LBL}>⏱️ مدة التنفيذ (مثلاً: 5 أيام عمل)</label><input name="durationAr" defaultValue={editing?.duration_ar || ''} placeholder="5 أيام عمل" style={INP} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151', cursor: 'pointer', fontWeight: 'bold' }}>
              <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
              ⭐ باقة مميزة (تظهر بشارة "الأكثر طلبًا")
            </label>
          </div>

          {/* الشرح */}
          <div style={secStyle}>
            <p style={secTitle}>📋 شرح الباقة</p>
            <textarea name="descriptionAr" defaultValue={editing?.description_ar || ''} rows="4" placeholder="اشرح الباقة بالتفصيل — عبارة عن إيه بالظبط؟" style={{ ...INP, resize: 'vertical', lineHeight: '1.8' }} />
          </div>

          {/* المميزات */}
          <div style={secStyle}>
            <p style={secTitle}>✨ مميزات الباقة (اللي العميل هيحصل عليه)</p>
            {features.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>✓</span>
                <input value={f} onChange={e => updateFeature(i, e.target.value)} placeholder="مثلاً: 3 تصميمات احترافية" style={{ ...INP, flex: 1 }} />
                {features.length > 1 && (
                  <button type="button" onClick={() => removeFeature(i)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>×</button>
                )}
              </div>
            ))}
            <button type="button" onClick={addFeature} style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>+ إضافة ميزة</button>
          </div>

          {/* الفيديو */}
          <div style={secStyle}>
            <p style={secTitle}>🎬 فيديو الباقة التوضيحي</p>
            <label style={LBL}>رابط الفيديو (Vimeo / YouTube / Drive)</label>
            <input name="videoUrl" defaultValue={editing?.video_url || ''} placeholder="https://vimeo.com/..." style={INP} />
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>الفيديو هيظهر للعميل في صفحة الباقة — "انت هتستلم نفس ده بالظبط"</p>
          </div>

          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={t} bg={G2} />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Role Modal ───────────────────────────────
export function RoleModal({ editing, t, onSubmit, onClose, saveLoad, rolePerms, setRolePerms }) {
  return (
    <Overlay onClose={onClose} dark>
      <Modal dark maxWidth="480px">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px', color: 'white' }}>🏷️ {editing ? t.editRole : t.addRole}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ height: '1px', background: '#1e293b', marginBottom: '18px' }} />
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div><label style={{ ...LBL, color: '#94a3b8', fontSize: '12px' }}>ID (slug)</label><input name="roleName" required defaultValue={editing?.name || ''} placeholder="editor" disabled={!!editing} style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: editing ? '#64748b' : 'white', fontSize: '13px' }} /></div>
            <div><label style={{ ...LBL, color: '#94a3b8', fontSize: '12px' }}>{t.roleNameAr}</label><input name="labelAr" required defaultValue={editing?.label_ar || ''} placeholder="محرر" style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white', fontSize: '13px' }} /></div>
            <div><label style={{ ...LBL, color: '#94a3b8', fontSize: '12px' }}>{t.roleNameEn}</label><input name="labelEn" required defaultValue={editing?.label_en || ''} placeholder="Editor" style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white', fontSize: '13px' }} /></div>
          </div>
          <PermsGrid state={rolePerms} setState={setRolePerms} permLabels={t.permLabels} PERM_KEYS={PERM_KEYS} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" disabled={saveLoad} style={{ flex: 1, padding: '11px', background: saveLoad ? '#334155' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: saveLoad ? '#64748b' : 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saveLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {saveLoad ? <Loader size={13} style={SS} /> : <Save size={13} />} {saveLoad ? t.saving : t.save}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '11px', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{t.cancel}</button>
          </div>
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Employee Modal ───────────────────────────
export function EmployeeModal({ editing, t, lang, onSubmit, onClose, saveLoad, empPerms, setEmpPerms, roles }) {
  return (
    <Overlay onClose={onClose} dark>
      <Modal dark maxWidth="500px" key={editing?.id || 'new'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '38px', height: '38px', background: GE, border: '2px solid #334155', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={18} color="#94a3b8" /></div>
            <div style={{ fontWeight: 'bold', fontSize: '15px', color: 'white' }}>{editing ? t.editEmployee : t.addEmployee}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ height: '1px', background: '#1e293b', marginBottom: '18px' }} />
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={{ ...LBL, color: '#94a3b8' }}>{t.nameAr}</label><input name="nameAr" required defaultValue={editing?.name_ar || ''} style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white' }} /></div>
            <div><label style={{ ...LBL, color: '#94a3b8' }}>{t.nameEn}</label><input name="nameEn" required defaultValue={editing?.name_en || ''} style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white' }} /></div>
          </div>
          <div><label style={{ ...LBL, color: '#94a3b8' }}>{t.employeeUsername}</label><input name="username" required defaultValue={editing?.username || ''} autoComplete="off" style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white' }} /></div>
          <div>
            <label style={{ ...LBL, color: '#94a3b8' }}>
              {t.employeePassword}
              {editing && <span style={{ color: '#64748b', fontSize: '11px', marginInlineStart: '6px' }}>({t.employeePasswordHint})</span>}
            </label>
            <input name="password" type="password" required={!editing} autoComplete="new-password" style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white' }} />
          </div>
          <div>
            <label style={{ ...LBL, color: '#94a3b8' }}>
              {t.employeeRole} <span style={{ color: '#475569', fontSize: '11px', marginInlineStart: '6px' }}>(بيطبّق الصلاحيات الافتراضية أوتوماتيك)</span>
            </label>
            <select name="roleId" required defaultValue={editing?.role_id || roles[0]?.id || 1}
              onChange={e => {
                const sel = roles.find(r => r.id === Number(e.target.value));
                if (sel) { const ps = {}; PERM_KEYS.forEach(k => ps[k] = sel[k] || false); setEmpPerms(ps); }
              }}
              style={{ ...INP, background: '#1e293b', border: '2px solid #334155', color: 'white', cursor: 'pointer' }}>
              {roles.map(r => <option key={r.id} value={r.id} style={{ background: '#1e293b' }}>{lang === 'ar' ? r.label_ar : r.label_en} — {r.name}</option>)}
            </select>
          </div>
          <PermsGrid state={empPerms} setState={setEmpPerms} permLabels={t.permLabels} PERM_KEYS={PERM_KEYS} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1e293b', padding: '10px', borderRadius: '8px', border: '1px solid #334155' }}>
            <input type="checkbox" name="isActive" id="isActive" defaultChecked={editing?.is_active !== false} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }} />
            <label htmlFor="isActive" style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '13px', fontWeight: 'bold' }}>● {t.employeeActive}</label>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" disabled={saveLoad} style={{ flex: 1, padding: '11px', background: saveLoad ? '#334155' : GE, color: saveLoad ? '#64748b' : 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saveLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {saveLoad ? <Loader size={13} style={SS} /> : <Save size={13} />} {saveLoad ? t.saving : t.save}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '11px', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{t.cancel}</button>
          </div>
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Settings Modal ───────────────────────────
export function SettingsModal({ settings, t, onSubmit, onClose, saveLoad, onOpenLabels, onUploadImage }) {
  const [logoUrl,  setLogoUrl]  = React.useState(settings.logo_url || '');
  const [logoLoad, setLogoLoad] = React.useState(false);
  const [instapayList, setInstapayList] = React.useState(() => {
    try {
      const arr = settings.instapay_numbers;
      if (Array.isArray(arr) && arr.length > 0) return arr;
      if (settings.instapay_number) return [{ label: 'InstaPay', number: settings.instapay_number }];
      return [{ label: 'InstaPay', number: '' }];
    } catch { return [{ label: 'InstaPay', number: settings.instapay_number || '' }]; }
  });

  const [socialLinks, setSocialLinks] = React.useState([]);
  const [socialLoad,  setSocialLoad]  = React.useState(true);

  // أخبار الشريط
  const [tickerItems, setTickerItems] = React.useState(() => {
    const arr = settings.ticker_items;
    return Array.isArray(arr) ? arr.map(it => ({ text: it.text || '', color: it.color || '#ffffff', link: it.link || '' })) : [];
  });
  const TICKER_COLORS = [
    { v: '#ffffff', n: 'أبيض' }, { v: '#ffd700', n: 'ذهبي' }, { v: '#90ee90', n: 'أخضر' },
    { v: '#f093fb', n: 'وردي' }, { v: '#60a5fa', n: 'أزرق' }, { v: '#fca5a5', n: 'أحمر' },
  ];
  const addTicker    = () => setTickerItems(p => [...p, { text: '', color: '#ffffff', link: '' }]);
  const removeTicker = (i) => setTickerItems(p => p.filter((_, idx) => idx !== i));
  const updateTicker = (i, field, val) => setTickerItems(p => p.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

  React.useEffect(() => {
    import('../../config/supabase').then(({ supabase }) => {
      supabase.from('social_links').select('*').order('order_num').then(({ data }) => {
        setSocialLinks(data || []);
        setSocialLoad(false);
      });
    });
  }, []);

  const PLATFORMS = [
    { value: 'facebook',  icon: '📘', label: 'Facebook' },
    { value: 'instagram', icon: '📸', label: 'Instagram' },
    { value: 'youtube',   icon: '▶️', label: 'YouTube' },
    { value: 'tiktok',    icon: '🎵', label: 'TikTok' },
    { value: 'linkedin',  icon: '💼', label: 'LinkedIn' },
    { value: 'twitter',   icon: '🐦', label: 'X / Twitter' },
    { value: 'whatsapp',  icon: '💬', label: 'WhatsApp' },
    { value: 'telegram',  icon: '✈️', label: 'Telegram' },
    { value: 'snapchat',  icon: '👻', label: 'Snapchat' },
    { value: 'custom',    icon: '🔗', label: 'رابط مخصص' },
  ];

  const addInstapay = () => setInstapayList(p => [...p, { label: '', number: '' }]);
  const removeInstapay = (i) => setInstapayList(p => p.filter((_, idx) => idx !== i));
  const updateInstapay = (i, field, val) => setInstapayList(p => p.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  const addSocial = () => setSocialLinks(p => [...p, { id: null, platform: 'facebook', label: '', url: '', icon: '📘', is_active: true, order_num: p.length }]);
  const removeSocial = async (idx, id) => {
    if (id) {
      const r = await contentDelete('social_links', id);
      if (r.error) { alert(r.error); return; }
    }
    setSocialLinks(p => p.filter((_, i) => i !== idx));
  };
  const updateSocial = (i, field, val) => setSocialLinks(p => p.map((item, idx) => {
    if (idx !== i) return item;
    const updated = { ...item, [field]: val };
    if (field === 'platform') {
      const plat = PLATFORMS.find(pl => pl.value === val);
      if (plat) { updated.icon = plat.icon; if (!item.label) updated.label = plat.label; }
    }
    return updated;
  }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoLoad(true);
    try {
      const url = onUploadImage ? await onUploadImage(file, 'logo') : null;
      if (url) setLogoUrl(url);
      else alert('فشل رفع اللوجو، حاول تاني');
    } catch (err) {
      console.error('Logo upload error:', err);
      alert('فشل رفع اللوجو، حاول تاني');
    } finally {
      setLogoLoad(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // حفظ السوشيال ميديا عبر السيرفر (content-save: insert/update حسب وجود id)
    let socialErr = '';
    for (const link of socialLinks) {
      if (!link.url.trim()) continue;
      const payload = {
        platform: link.platform, label: link.label, url: link.url,
        icon: link.icon, is_active: link.is_active, order_num: link.order_num,
      };
      if (link.id) payload.id = link.id;
      const r = await contentSave('social_links', payload);
      if (r.ok && r.row) link.id = r.row.id;
      else if (r.error) socialErr = r.error;
    }
    if (socialErr) alert('فيه روابط سوشيال ما اتحفظتش: ' + socialErr);
    onSubmit(e, instapayList.filter(i => i.number.trim()), tickerItems.filter(it => it.text.trim()));
  };

  const secStyle = { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' };
  const secTitle = { fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="560px">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>{t.generalSettings}</h2>
          {onOpenLabels && (
            <button type="button" onClick={onOpenLabels}
              style={{ padding: '7px 14px', background: 'linear-gradient(135deg,#5a67d8,#764ba2)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
              🏷️ مدير المسميات
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '75vh', overflowY: 'auto', paddingLeft: '4px' }}>

          {/* ── معلومات الموقع ── */}
          <div style={secStyle}>
            <p style={secTitle}>🌐 معلومات الموقع</p>

            {/* لوجو الموقع */}
            <div>
              <label style={LBL}>🖼️ لوجو الموقع</label>
              <input type="hidden" name="logoUrl" value={logoUrl} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ width: 60, height: 60, borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {logoUrl
                    ? <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 26 }}>💎</span>}
                </div>
                <label style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#5a67d8,#764ba2)', color: '#fff', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: logoLoad ? 'wait' : 'pointer', fontFamily: 'Cairo, sans-serif', opacity: logoLoad ? 0.6 : 1 }}>
                  {logoLoad ? '... جاري الرفع' : '📤 رفع لوجو'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoLoad} style={{ display: 'none' }} />
                </label>
                {logoUrl && (
                  <button type="button" onClick={() => setLogoUrl('')}
                    style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                    🗑️ حذف
                  </button>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '6px 0 0' }}>يُفضّل صورة مربعة (PNG شفاف) — هتظهر في الهيدر بجانب اسم الموقع</p>
            </div>

            <div><label style={LBL}>{t.siteName}</label><input name="siteName" required defaultValue={settings.site_name} style={INP} /></div>
            <div><label style={LBL}>📧 البريد الإلكتروني</label><input name="emailContact" defaultValue={settings.email_contact || ''} type="email" placeholder="info@gawhara.com" style={INP} /></div>
            <div><label style={LBL}>📍 العنوان</label><input name="address" defaultValue={settings.address || ''} placeholder="القاهرة، مصر" style={INP} /></div>
          </div>

          {/* ── واتساب ── */}
          <div style={secStyle}>
            <p style={secTitle}>💬 واتساب</p>
            <div><label style={LBL}>{t.whatsappNumber}</label><input name="whatsappNumber" required defaultValue={settings.whatsapp_number} style={INP} /></div>
            <div><label style={LBL}>{t.whatsappMessage}</label><textarea name="whatsappMessage" required defaultValue={settings.whatsapp_message} rows="2" style={{ ...INP, resize: 'vertical' }} /></div>
          </div>

          {/* ── الدفع ── */}
          <div style={secStyle}>
            <p style={secTitle}>💳 وسائل الدفع</p>
            <div><label style={LBL}>{t.vodafoneNumber}</label><input name="vodafoneNumber" defaultValue={settings.vodafone_number || ''} style={INP} /></div>

            {/* InstaPay متعدد */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={LBL}>💳 أرقام InstaPay</label>
                <button type="button" onClick={addInstapay}
                  style={{ padding: '4px 12px', background: '#eef2ff', color: '#5a67d8', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                  + إضافة رقم
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {instapayList.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input placeholder="الوصف (مثال: الجوهرة الرئيسي)" value={item.label}
                      onChange={e => updateInstapay(i, 'label', e.target.value)}
                      style={{ ...INP, flex: 1, margin: 0 }} />
                    <input placeholder="الرقم 01xxxxxxxx" value={item.number} dir="ltr"
                      onChange={e => updateInstapay(i, 'number', e.target.value)}
                      style={{ ...INP, flex: 1, margin: 0 }} />
                    {instapayList.length > 1 && (
                      <button type="button" onClick={() => removeInstapay(i)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div><label style={LBL}>{t.bankAccount}</label><textarea name="bankAccount" defaultValue={settings.bank_account || ''} rows="2" style={{ ...INP, resize: 'vertical' }} placeholder="اسم البنك — رقم الحساب — صاحب الحساب" /></div>
          </div>

          {/* ── السوشيال ميديا ── */}
          <div style={secStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ ...secTitle, marginBottom: 0 }}>📱 حسابات السوشيال ميديا</p>
              <button type="button" onClick={addSocial}
                style={{ padding: '4px 12px', background: '#eef2ff', color: '#5a67d8', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                + إضافة حساب
              </button>
            </div>

            {socialLoad ? <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>جاري التحميل...</p>
            : socialLinks.length === 0 ? <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>لا توجد حسابات — اضغط "+ إضافة حساب"</p>
            : socialLinks.map((link, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ ...LBL, fontSize: '11px' }}>المنصة</label>
                    <select value={link.platform} onChange={e => updateSocial(i, 'platform', e.target.value)} style={{ ...INP, fontSize: '12px', margin: 0 }}>
                      {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ ...LBL, fontSize: '11px' }}>اسم الحساب</label>
                    <input value={link.label} onChange={e => updateSocial(i, 'label', e.target.value)}
                      placeholder={`مثال: ${PLATFORMS.find(p=>p.value===link.platform)?.label} الجوهرة`}
                      style={{ ...INP, margin: 0 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...LBL, fontSize: '11px' }}>الرابط</label>
                    <input value={link.url} onChange={e => updateSocial(i, 'url', e.target.value)}
                      placeholder="https://..." dir="ltr"
                      style={{ ...INP, margin: 0 }} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', paddingBottom: '2px' }}>
                    <button type="button" onClick={() => updateSocial(i, 'is_active', !link.is_active)}
                      style={{ padding: '7px 10px', background: link.is_active ? '#dcfce7' : '#f3f4f6', color: link.is_active ? '#16a34a' : '#9CA3AF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                      {link.is_active ? '✓' : '—'}
                    </button>
                    <button type="button" onClick={() => removeSocial(i, link.id)}
                      style={{ padding: '7px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── شريط الأخبار ── */}
          <div style={secStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ ...secTitle, marginBottom: 0 }}>📢 شريط الأخبار</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input name="tickerEnabled" type="checkbox" defaultChecked={settings.ticker_enabled !== false} />
                تفعيل
              </label>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 4px' }}>الأخبار اللي بتظهر متحركة فوق الموقع — أضف/عدّل/احذف من هنا 👇</p>

            {tickerItems.length === 0 && (
              <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', padding: '8px', background: '#fff', borderRadius: '8px', margin: 0 }}>لا توجد أخبار — اضغط "+ إضافة خبر"</p>
            )}

            {tickerItems.map((it, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: it.color, border: '1px solid #d1d5db', flexShrink: 0 }} />
                  <input value={it.text} onChange={e => updateTicker(i, 'text', e.target.value)} placeholder="نص الخبر — مثلاً: خصم 20% على كل الكورسات 🔥" style={{ ...INP, margin: 0, flex: 1 }} />
                  <button type="button" onClick={() => removeTicker(i)} style={{ padding: '7px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {TICKER_COLORS.map(c => (
                      <button key={c.v} type="button" onClick={() => updateTicker(i, 'color', c.v)} title={c.n}
                        style={{ width: '22px', height: '22px', borderRadius: '50%', background: c.v, border: it.color === c.v ? '2.5px solid #764ba2' : '1px solid #d1d5db', cursor: 'pointer', padding: 0 }} />
                    ))}
                  </div>
                  <select value={['academy','agency','contact','mycourses',''].includes(it.link) ? it.link : ''} onChange={e => updateTicker(i, 'link', e.target.value)}
                    style={{ ...INP, margin: 0, fontSize: '12px', flex: 1, minWidth: '130px' }}>
                    <option value="">بدون رابط</option>
                    <option value="academy">يفتح: الأكاديمية</option>
                    <option value="agency">يفتح: الأجنسي</option>
                    <option value="contact">يفتح: تواصل معنا</option>
                    <option value="mycourses">يفتح: كورساتي</option>
                  </select>
                </div>
              </div>
            ))}

            <button type="button" onClick={addTicker}
              style={{ padding: '9px', background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
              + إضافة خبر
            </button>
          </div>

          {/* ── قسم من نحن ── */}
          <div style={secStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <p style={{ ...secTitle, marginBottom: 0 }}>👥 قسم من نحن</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input name="aboutEnabled" type="checkbox" defaultChecked={settings.about_enabled !== false} />
                تفعيل
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div><label style={LBL}>العنوان عربي</label><input name="aboutTitleAr" defaultValue={settings.about_title_ar || 'من نحن'} style={INP} /></div>
              <div><label style={LBL}>العنوان إنجليزي</label><input name="aboutTitleEn" defaultValue={settings.about_title_en || 'About Us'} dir="ltr" style={INP} /></div>
            </div>
            <div><label style={LBL}>النص عربي</label><textarea name="aboutTextAr" defaultValue={settings.about_text_ar || ''} rows="3" style={{ ...INP, resize: 'vertical' }} placeholder="اكتب عن الأكاديمية..." /></div>
            <div><label style={LBL}>النص إنجليزي</label><textarea name="aboutTextEn" defaultValue={settings.about_text_en || ''} rows="3" dir="ltr" style={{ ...INP, resize: 'vertical' }} placeholder="Write about the academy..." /></div>

            {/* إحصاءات قابلة للتعديل */}
            <AboutStatsEditor settings={settings} /></div>

          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={t} bg={GG} />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Shared button row ────────────────────────
function SubmitRow({ saveLoad, onClose, t, bg }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button type="submit" disabled={saveLoad} style={{ flex: 1, padding: '11px', background: saveLoad ? '#9CA3AF' : bg, color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saveLoad ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
        {saveLoad ? <Loader size={13} style={SS} /> : <Save size={13} />} {saveLoad ? t.saving : t.save}
      </button>
      <button type="button" onClick={onClose} style={{ flex: 1, padding: '11px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>{t.cancel}</button>
    </div>
  );
}

// ── Coupon Modal (Dashboard) ─────────────────
export function CouponModal({ editing, onSubmit, onClose, saveLoad, courses, lang }) {
  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="460px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>
          {editing ? '✏️ تعديل كوبون' : '🎟️ إضافة كوبون جديد'}
        </h2>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={LBL}>كود الخصم</label>
            <input name="code" required defaultValue={editing?.code} placeholder="مثال: GAWHARA20"
              style={{ ...INP, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}
              onChange={e => { e.target.value = e.target.value.toUpperCase(); }} />
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>حروف إنجليزية وأرقام فقط، بدون مسافات</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={LBL}>نوع الخصم</label>
              <select name="type" defaultValue={editing?.type || 'percent'} style={{ ...INP }}>
                <option value="percent">نسبة % (مئوية)</option>
                <option value="fixed">مبلغ ثابت (جنيه)</option>
              </select>
            </div>
            <div>
              <label style={LBL}>قيمة الخصم</label>
              <input name="value" type="number" min="1" required defaultValue={editing?.value}
                placeholder="20" style={INP} />
            </div>
          </div>
          <div>
            <label style={LBL}>ينطبق على</label>
            <select name="course_id" defaultValue={editing?.course_id || ''} style={INP}>
              <option value="">🌍 كل الكورسات</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{lang === 'ar' ? c.name_ar : c.name_en}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={LBL}>أقصى عدد استخدامات</label>
              <input name="max_uses" type="number" min="1" defaultValue={editing?.max_uses || ''}
                placeholder="بلا حد" style={INP} />
            </div>
            <div>
              <label style={LBL}>تاريخ انتهاء الصلاحية</label>
              <input name="expires_at" type="date" defaultValue={editing?.expires_at ? editing.expires_at.substring(0,10) : ''}
                style={INP} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#F9FAFB', borderRadius: '8px' }}>
            <input name="is_active" type="checkbox" defaultChecked={editing?.is_active !== false} id="is_active" style={{ width: '16px', height: '16px' }} />
            <label htmlFor="is_active" style={{ fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>الكوبون مفعّل</label>
          </div>
          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={{ save: 'حفظ', saving: 'جاري الحفظ...', cancel: 'إلغاء' }} bg="linear-gradient(135deg,#5a67d8,#764ba2)" />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Category Modal (التصنيفات) ─────────────────
export function CategoryModal({ editing, onSubmit, onClose, saveLoad }) {
  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="420px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>{editing ? 'تعديل تصنيف' : 'إضافة تصنيف'}</h2>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div><label style={LBL}>الاسم بالعربي</label><input name="nameAr" required defaultValue={editing?.name_ar} placeholder="مثلاً: ذكاء اصطناعي" style={INP} /></div>
          <div><label style={LBL}>الاسم بالإنجليزي</label><input name="nameEn" defaultValue={editing?.name_en || ''} placeholder="AI" style={INP} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={LBL}>أيقونة (Emoji)</label><input name="icon" defaultValue={editing?.icon || '📚'} style={INP} /></div>
            <div><label style={LBL}>الترتيب</label><input name="orderNum" type="number" defaultValue={editing?.order_num || 0} style={INP} /></div>
          </div>
          <div><label style={LBL}>وصف مختصر (اختياري)</label><input name="descriptionAr" defaultValue={editing?.description_ar || ''} placeholder="نبذة عن التصنيف" style={INP} /></div>
          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={{ save: 'حفظ', saving: 'جاري الحفظ...', cancel: 'إلغاء' }} bg={G1} />
        </form>
      </Modal>
    </Overlay>
  );
}

// ── Instructor Modal (المدربين) ─────────────────
export function InstructorModal({ editing, onSubmit, onClose, saveLoad, onUploadImage }) {
  const [avatar, setAvatar] = useState(editing?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const avRef = React.useRef();

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setErr('الصورة أكبر من 3MB'); return; }
    setErr(''); setUploading(true);
    const url = await onUploadImage(file, 'instructor');
    if (url) setAvatar(url); else setErr('فشل الرفع');
    setUploading(false);
  };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="480px">
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '18px', color: '#1F2937' }}>{editing ? 'تعديل مدرب' : 'إضافة مدرب'}</h2>
        <form onSubmit={(e) => onSubmit(e, avatar)} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '70vh', overflowY: 'auto', paddingLeft: '4px' }}>
          {/* صورة المدرب */}
          <div style={{ textAlign: 'center' }}>
            <div onClick={() => avRef.current?.click()}
              style={{ width: '90px', height: '90px', borderRadius: '50%', margin: '0 auto', cursor: 'pointer', border: '2px dashed #C7D2FE', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <input ref={avRef} type="file" accept="image/*" onChange={handleAvatar} style={{ display: 'none' }} />
              {uploading ? <Loader size={22} color="#667eea" style={SS} />
                : avatar ? <img src={avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '11px', color: '#667eea' }}>📤 صورة</span>}
            </div>
            {err && <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>{err}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={LBL}>الاسم بالعربي</label><input name="nameAr" required defaultValue={editing?.name_ar} placeholder="بشمهندس ميدو" style={INP} /></div>
            <div><label style={LBL}>الاسم بالإنجليزي</label><input name="nameEn" defaultValue={editing?.name_en || ''} style={INP} /></div>
          </div>
          <div><label style={LBL}>المسمى الوظيفي</label><input name="titleAr" defaultValue={editing?.title_ar || ''} placeholder="خبير ذكاء اصطناعي" style={INP} /></div>
          <div><label style={LBL}>نبذة / السيرة الذاتية</label><textarea name="bioAr" defaultValue={editing?.bio_ar || ''} placeholder="اكتب نبذة كاملة عن المدرب وخبراته..." style={{ ...INP, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }} /></div>
          <div><label style={LBL}>الخبرات (سطر لكل خبرة)</label><textarea name="experienceAr" defaultValue={editing?.experience_ar || ''} placeholder={'10 سنين خبرة\nدرّب 5000 طالب\nشهادات معتمدة'} style={{ ...INP, minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' }} /></div>
          <div><label style={LBL}>التخصصات</label><input name="specialties" defaultValue={editing?.specialties || ''} placeholder="ذكاء اصطناعي، برمجة، تحليل بيانات" style={INP} /></div>
          <div><label style={LBL}>الترتيب</label><input name="orderNum" type="number" defaultValue={editing?.order_num || 0} style={INP} /></div>
          <SubmitRow saveLoad={saveLoad} onClose={onClose} t={{ save: 'حفظ', saving: 'جاري الحفظ...', cancel: 'إلغاء' }} bg={G1} />
        </form>
      </Modal>
    </Overlay>
  );
}
